import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { api, apiPharmacies, pharmaciesBaseRoute, IAccount, IPharmacy } from '../../types';
import { createApiHeaders, parseJwtToken } from '../../utilities/JwtTokenParser';
import Header from '../../components/Header';
import { useAlert } from '../../layout/MainAlert';

const pharmacyPrototype : IPharmacy = {
  id: 0, 
  subscriptionCode: "",
  name: "",
  address: "",
  city: "",
  state: "",
  postalCode: "",
  phone: "",
  email: "", 
  accountId: 0,
  isAccountDefault: false
}

const fetchPharmacyData = async (pharmacyId: string): Promise<IPharmacy> => {
  const data = await fetch(`${api}${apiPharmacies}${pharmacyId}`, {
    method: 'GET', 
    headers: createApiHeaders(true, false)
  });
  
  const json = await data.json();
  return json;
}

const savePharmacyData = async (accountId: number, pharmacy: IPharmacy): Promise<IPharmacy> => {
  pharmacy.accountId = accountId;
  const response = await fetch(`${api}${apiPharmacies}${pharmacy.id === 0 ? "" : pharmacy.id}`, {
    method: pharmacy.id === 0 ? 'POST' : 'PUT',
    headers: createApiHeaders(true, false),
    body: JSON.stringify(pharmacy)
  });

  if (response.ok) {
    const json = await response.json();
    return json;
  }

  throw new Error(`Detailed Error Code: ${response.status}.`);
}

const validationSchema = yup.object().shape({
  name: yup.string()
    .required("Name is required")
    .min(4, "Name must be with a minimum length of 4 and a maximum length of 1024")
    .max(1024, "Name must be with a minimum length of 4 and a maximum length of 1024"),
  address: yup.string()
    .required("Address is required")
    .min(8, "Address must be with a minimum length of 8 and a maximum length of 1024")
    .max(1024, "Address must be with a minimum length of 8 and a maximum length of 1024"),
  city: yup.string()
    .required("City is required")
    .min(2, "City must be with a minimum length of 2 and a maximum length of 256")
    .max(256, "City must be with a minimum length of 2 and a maximum length of 256"),
  state: yup.string()
    .required("Province is required")
    .test("len", "Province must be with an exact length of 2", value => value.length === 2),
  postalCode: yup.string()
    .required("Postal is required")
    .test("len", "Postal must be with an exact length of 6", value => value.length === 6),
  phone: yup.string()
    .required("Phone is required"),
  email: yup.string()
    .required("Email is required")
    .email("Email must be in correct format")
});

const PharmacyEdit = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const alert = useAlert();

  const account = parseJwtToken() as IAccount;
  const params = useParams();
  const pharmacyId = params.id;
  const [pharmacy, setPharmacy] = useState<IPharmacy>(pharmacyPrototype);

  useEffect(() => {
    if (pharmacyId && parseInt(pharmacyId) > 0) {
      fetchPharmacyData(pharmacyId)
      .then((response) => { 
        setPharmacy(response as IPharmacy); 
      })
      .catch(console.error);
    }
  }, []);

  const formik = useFormik({
    initialValues: pharmacy,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      savePharmacyData(account.id, values)
      .then((response) => {
        alert.success(`Pharmacy "${response.name}" saved successfully.`);
        navigate(`${pharmaciesBaseRoute}`);
      })
      .catch((error) => {
        alert.error(`Pharmacy can't be saved. ${error}`);
      });
    }
  });
  
  return (
    <Box m="14px">
      <Header title="PHARMACY INFO" subtitle="" />

      <form onSubmit={formik.handleSubmit}>
        <Box 
          display="grid" 
          gridTemplateColumns="repeat(6, minmax(0, 1fr))"
          gap="20px" 
          sx={{
            "& > div": { gridColumn: isNonMobile ? undefined : "span 6" }
          }}
        >
          <TextField
            fullWidth
            variant="filled"
            id="name"
            name="name"
            label="Name"
            color="info"
            value={formik.values.name}
            onChange={formik.handleChange} 
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            sx={{ gridColumn: "span 6" }}
          />
          
          <TextField 
            fullWidth
            variant="filled"
            id="address"
            name="address"
            label="Address"
            color="info"
            value={formik.values.address}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.address && Boolean(formik.errors.address)}
            helperText={formik.touched.address && formik.errors.address}
            sx={{ gridColumn: "span 6" }}
          />

          <TextField
            fullWidth
            variant="filled"
            id="city"
            name="city"
            label="City"
            color="info"
            value={formik.values.city}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.city && Boolean(formik.errors.city)}
            helperText={formik.touched.city && formik.errors.city}
            sx={{ gridColumn: "span 2" }}
          />

          <TextField
            fullWidth
            variant="filled"
            id="state"
            name="state"
            label="Province"
            color="info"
            value={formik.values.state}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.state && Boolean(formik.errors.state)}
            helperText={formik.touched.state && formik.errors.state}
            sx={{ gridColumn: "span 2" }}
          />

          <TextField
            fullWidth
            variant="filled"
            id="postalCode"
            name="postalCode"
            label="Postal"
            color="info"
            value={formik.values.postalCode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.postalCode && Boolean(formik.errors.postalCode)}
            helperText={formik.touched.postalCode && formik.errors.postalCode}
            sx={{ gridColumn: "span 2" }}
          />

          <TextField
            fullWidth
            variant="filled"
            id="phone"
            name="phone"
            label="Phone"
            color="info"
            value={formik.values.phone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={formik.touched.phone && formik.errors.phone}
            sx={{ gridColumn: "span 2" }}
          />

          <TextField
            fullWidth
            variant="filled"
            id="email"
            name="email"
            label="Email"
            color="info"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            sx={{ gridColumn: "span 4" }}
          />

          <FormControlLabel 
            label="Is Default" 
            control={
              <Switch
                id="isAccountDefault"
                name="isAccountDefault"
                color="info" 
                checked={formik.values.isAccountDefault}
                onChange={formik.handleChange} 
                onBlur={formik.handleBlur}
              />
            }
            sx={{ gridColumn: "span 1" }}
          />
        </Box>
            
        <Box display="flex" justifyContent="end" mt="20px">
          <Button 
            type="submit" 
            variant="contained" 
            color="secondary"
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              padding: "8px 14px",
            }} 
          >
            Save Changes
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default PharmacyEdit;